import {
  ALL_TIME_DATE_RANGE_LABEL,
  ONE_YEAR_DATE_RANGE_LABEL,
  TWO_YEAR_DATE_RANGE_LABEL,
} from 'app/stores/RasterCalculationStore';
import {
  CDL_LANDCOVER,
  CHEATGRASS_PERCENT_COVER,
  CHLORIS_10_BIOMASS_STOCK,
  CHLORIS_10_FOREST_COVER,
  CHLORIS_30_BIOMASS_STOCK,
  CHLORIS_30_FOREST_COVER,
  CHLORIS_BIOMASS_STOCK,
  EAG_PERCENT_COVER,
  ESA_BIOMASS_STOCK,
  ESA_WORLDCOVER_LANDCOVER,
  GEFS_ANALYSIS_MONTHLY_TEMPERATURE_MEAN,
  GLOBAL_FOREST_CHANGE_LOSSYEAR,
  GLOBAL_FOREST_COVER_FORESTCOVER,
  GNATSGO_SOILTYPE,
  GRASSLANDS_RISK,
  IMPACT_OBSERVATORY_BIODIVERSITY,
  IMPACT_OBSERVATORY_HIGH_RES_LANDCOVER,
  IMPACT_OBSERVATORY_LANDCOVER,
  L8_NDBI,
  MAPBIOMAS_BRAZIL_LANDCOVER,
  MAPBIOMAS_CHACO_LANDCOVER,
  MAPBIOMAS_COLOMBIA_LANDCOVER,
  METAWRI_CANOPY_HEIGHT,
  NLCD_ANNUAL_FRACTIONAL_IMPERVIOUS_SURFACE,
  NLCD_ANNUAL_LANDCOVER,
  NLCD_LANDCOVER,
  PLANET_FOREST_ABOVEGROUND_CARBON,
  PLANET_FOREST_ABOVEGROUND_CARBON_3M,
  PLANET_FOREST_CANOPY_COVER,
  PLANET_FOREST_CANOPY_HEIGHT,
  PRISM_M_PRECIPITATION,
  RCMAP_CHEATGRASS,
  RCMAP_FIELD_BROME,
  RCMAP_GREEN_HERBACEOUS,
  RCMAP_MEDUSAHEAD,
  RCMAP_SANDBERGS_BLUEGRASS,
  RCMAP_SENESCED_HERBACEOUS,
  RCMAP_TOTAL_EAG,
  RCMAP_TOTAL_HERBACEOUS,
  S2_NDBI,
  SALO_ABOVEGROUND_CARBON_30M,
  SALO_ABOVEGROUND_CARBON_3M,
  SBTN_NATURAL_LANDCOVER,
  SPACE_INTELLIGENCE_CARBON,
  SPACE_INTELLIGENCE_HABITAT,
  SPACE_INTELLIGENCE_HABITAT_10_CLASS,
  TMF_ANNUAL_CHANGE_LANDCOVER,
  WLFW_CO_ISI,
  WRI_GLOBAL_GRASSLAND_PASTURE_LANDCOVER,
} from 'app/utils/layers';

import {ExtraGraphOptions, GraphMode} from './AnalyzePolygonPopup';
import {GraphRange} from '../AnalyzePolygonChart/types';

export interface LayerSetting {
  graphModes?: GraphMode[];
  defaultGraphMode?: GraphMode;
  graphRanges?: GraphRange[];
  defaultGraphRange?: GraphRange;
}
export type LayerSettings = Record<string, LayerSetting>;

export const DEFAULT_GRAPH_MODES: GraphMode[] = ['average', 'averageMonthly', 'area'];
export const DEFAULT_GRAPH_MODE = 'average' as const;
export const DEFAULT_GRAPH_RANGES: GraphRange[] = [
  {type: 'simple', label: ONE_YEAR_DATE_RANGE_LABEL},
  {type: 'simple', label: TWO_YEAR_DATE_RANGE_LABEL},
  {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  {type: 'custom', range: [null, null]},
  {type: 'by-year', yearType: 'calendar'},
];
export const DEFAULT_GRAPH_RANGE_FOR_ANNUAL_LAYERS: GraphRange[] = [
  {type: 'simple', label: TWO_YEAR_DATE_RANGE_LABEL},
  {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  {type: 'custom', range: [null, null]},
];
export const DEFAULT_GRAPH_RANGE = {type: 'simple', label: ONE_YEAR_DATE_RANGE_LABEL} as const;

export const DEFAULT_LAYER_SETTING: LayerSetting = {
  graphModes: DEFAULT_GRAPH_MODES,
  defaultGraphMode: DEFAULT_GRAPH_MODE,
  graphRanges: DEFAULT_GRAPH_RANGES,
  defaultGraphRange: DEFAULT_GRAPH_RANGE,
};

export const DEFAULT_EXTRA_GRAPH_OPTIONS: ExtraGraphOptions = {
  showCumulativeValues: false,
};

const CATEGORY_LAYER_DEFAULT_GRAPH_SETTING: LayerSetting = {
  graphModes: ['areaByCategory'],
  defaultGraphMode: 'areaByCategory',
  graphRanges: [{type: 'some-years', options: []}],
  defaultGraphRange: {type: 'some-years', options: []},
};

// All eight RCMAP components are continuous percent cover on the same weekly cadence.
const RCMAP_PERCENT_COVER_GRAPH_SETTING: LayerSetting = {
  graphModes: ['average', 'area'],
  defaultGraphMode: 'average',
};

/**
 * Utility function for getting custom layer settings.
 */
export function getLayerSettings(layerKey?: string) {
  return (layerKey && LAYER_SETTINGS[layerKey]) || {};
}

/**
 * Custom layer settings specifically for Analyze Area and related interfaces.
 */
export const LAYER_SETTINGS: LayerSettings = {
  [SALO_ABOVEGROUND_CARBON_3M]: {
    graphModes: ['aggregateCarbonSalo'],
    defaultGraphMode: 'aggregateCarbonSalo',
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [SALO_ABOVEGROUND_CARBON_30M]: {
    graphModes: ['aggregateCarbonSalo'],
    defaultGraphMode: 'aggregateCarbonSalo',
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [SPACE_INTELLIGENCE_CARBON]: {
    graphModes: ['aggregateCarbonSpaceIntelligence'],
    defaultGraphMode: 'aggregateCarbonSpaceIntelligence',
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [PLANET_FOREST_ABOVEGROUND_CARBON]: {
    graphModes: ['aggregateCarbonSalo', 'area'],
    defaultGraphMode: 'aggregateCarbonSalo',
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [PLANET_FOREST_ABOVEGROUND_CARBON_3M]: {
    graphModes: ['aggregateCarbonSalo', 'area'],
    defaultGraphMode: 'aggregateCarbonSalo',
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [PLANET_FOREST_CANOPY_COVER]: {
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [PLANET_FOREST_CANOPY_HEIGHT]: {
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [CHLORIS_BIOMASS_STOCK]: {
    graphModes: ['aggregateCarbonSalo', 'area'],
    defaultGraphMode: 'aggregateCarbonSalo',
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [CHLORIS_30_BIOMASS_STOCK]: {
    graphModes: ['aggregateCarbonSalo', 'area'],
    defaultGraphMode: 'aggregateCarbonSalo',
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [CHLORIS_30_FOREST_COVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [CHLORIS_10_BIOMASS_STOCK]: {
    graphModes: ['aggregateCarbonSalo', 'area'],
    defaultGraphMode: 'aggregateCarbonSalo',
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [CHLORIS_10_FOREST_COVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [PRISM_M_PRECIPITATION]: {defaultGraphMode: 'averageMonthly'},
  [GEFS_ANALYSIS_MONTHLY_TEMPERATURE_MEAN]: {defaultGraphMode: 'averageMonthly'},

  [S2_NDBI]: {
    graphModes: ['average', 'area'],
    defaultGraphMode: 'average',
  },
  [L8_NDBI]: {
    graphModes: ['average', 'area'],
    defaultGraphMode: 'average',
  },
  [NLCD_ANNUAL_FRACTIONAL_IMPERVIOUS_SURFACE]: {
    graphModes: ['average', 'area'],
    defaultGraphMode: 'average',
    graphRanges: DEFAULT_GRAPH_RANGE_FOR_ANNUAL_LAYERS,
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [METAWRI_CANOPY_HEIGHT]: {
    graphModes: ['average', 'area'],
    defaultGraphMode: 'average',
  },
  [NLCD_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [NLCD_ANNUAL_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [GNATSGO_SOILTYPE]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [ESA_BIOMASS_STOCK]: {
    graphModes: ['aggregateCarbonSalo', 'area'],
    defaultGraphMode: 'aggregateCarbonSalo',
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [ESA_WORLDCOVER_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [SPACE_INTELLIGENCE_HABITAT]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [SPACE_INTELLIGENCE_HABITAT_10_CLASS]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [IMPACT_OBSERVATORY_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [IMPACT_OBSERVATORY_HIGH_RES_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [MAPBIOMAS_BRAZIL_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [MAPBIOMAS_CHACO_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [MAPBIOMAS_COLOMBIA_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [SBTN_NATURAL_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [TMF_ANNUAL_CHANGE_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [GLOBAL_FOREST_CHANGE_LOSSYEAR]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [GLOBAL_FOREST_COVER_FORESTCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [IMPACT_OBSERVATORY_BIODIVERSITY]: {
    graphModes: ['average', 'area'],
    defaultGraphMode: 'average',
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [CDL_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [WRI_GLOBAL_GRASSLAND_PASTURE_LANDCOVER]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [EAG_PERCENT_COVER]: {
    graphModes: ['average', 'area'],
    defaultGraphMode: 'average',
  },
  [CHEATGRASS_PERCENT_COVER]: {
    graphModes: ['average', 'area'],
    defaultGraphMode: 'average',
    graphRanges: DEFAULT_GRAPH_RANGE_FOR_ANNUAL_LAYERS,
    defaultGraphRange: {type: 'simple', label: ALL_TIME_DATE_RANGE_LABEL},
  },
  [RCMAP_TOTAL_HERBACEOUS]: RCMAP_PERCENT_COVER_GRAPH_SETTING,
  [RCMAP_GREEN_HERBACEOUS]: RCMAP_PERCENT_COVER_GRAPH_SETTING,
  [RCMAP_SENESCED_HERBACEOUS]: RCMAP_PERCENT_COVER_GRAPH_SETTING,
  [RCMAP_TOTAL_EAG]: RCMAP_PERCENT_COVER_GRAPH_SETTING,
  [RCMAP_CHEATGRASS]: RCMAP_PERCENT_COVER_GRAPH_SETTING,
  [RCMAP_FIELD_BROME]: RCMAP_PERCENT_COVER_GRAPH_SETTING,
  [RCMAP_MEDUSAHEAD]: RCMAP_PERCENT_COVER_GRAPH_SETTING,
  [RCMAP_SANDBERGS_BLUEGRASS]: RCMAP_PERCENT_COVER_GRAPH_SETTING,
  [GRASSLANDS_RISK]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
  [WLFW_CO_ISI]: CATEGORY_LAYER_DEFAULT_GRAPH_SETTING,
};
